import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProspectCard = ({ prospect, onDragStart, onViewDetails, calculateTimeInStage }) => {
  const getPriorityColor = () => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[prospect?.priority] || colors?.medium;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const daysInStage = calculateTimeInStage(prospect?.stageEntryDate);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="bg-background rounded-xl border border-border p-4 hover:border-primary/30 hover:shadow-md transition-all cursor-move"
    >
      {/* Company Info */}
      <div className="flex items-start gap-3 mb-3">
        <Image
          src={prospect?.avatar}
          alt={prospect?.avatarAlt}
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground text-sm line-clamp-1 mb-1">
            {prospect?.companyName}
          </h4>
          <p className="text-xs text-muted-foreground line-clamp-1">
            {prospect?.contactName}
          </p>
        </div>
        <div className={`px-2 py-1 rounded-lg border text-xs font-medium ${getPriorityColor()}`}>
          {prospect?.priority}
        </div>
      </div>

      {/* Deal Value */}
      <div className="mb-3 p-2 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Deal Value</span>
          <span className="text-sm font-bold text-foreground">{formatCurrency(prospect?.dealValue)}</span>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className="text-xs bg-muted px-2 py-1 rounded-lg flex items-center gap-1">
          <Icon name="Users" size={12} className="text-muted-foreground" />
          {prospect?.teamSize}
        </span>
        <span className="text-xs bg-muted px-2 py-1 rounded-lg flex items-center gap-1">
          <Icon name="Building2" size={12} className="text-muted-foreground" />
          {prospect?.industry}
        </span>
      </div>

      {/* Time in Stage */}
      <div className="flex items-center gap-2 mb-3 p-2 bg-accent/5 rounded-lg">
        <Icon name="Clock" size={14} className="text-accent" />
        <span className="text-xs text-muted-foreground">
          {daysInStage} {daysInStage === 1 ? 'day' : 'days'} in stage
        </span>
      </div>

      {/* Next Action */}
      {prospect?.nextAction && (
        <div className="mb-3 p-2 bg-primary/5 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Target" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-foreground line-clamp-2">{prospect?.nextAction}</p>
          </div>
        </div>
      )}

      {/* Probability Score */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Probability</span>
          <span className="text-xs font-medium text-foreground">{prospect?.probability}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-primary to-success h-1.5 rounded-full transition-all"
            style={{ width: `${prospect?.probability}%` }}
          />
        </div>
      </div>

      {/* Actions */}
      <Button
        variant="outline"
        size="sm"
        iconName="Eye"
        iconPosition="left"
        onClick={onViewDetails}
        fullWidth
      >
        View Details
      </Button>
    </div>
  );
};

export default ProspectCard;